import { useMemo } from "react";
import { CompactStatsTable } from "@/components/general/Dashboard/Overview/CompactStatsTable";
import { OverviewStatsTable } from "@/components/general/Dashboard/Overview/OverviewStatsTable";
import { periodTableColumns, type PeriodTableRow } from "@/components/general/Dashboard/Overview/PeriodTableColumns";
import { StatsMobileCards, type StatRow } from "@/components/general/Dashboard/Overview/AnalyticsMobileCards";
import ComparisonEngineChart, { type RadarPoint } from "@/components/general/Dashboard/Comparison/ComparisonEngineChart";
import type { LinePeriodData, PlayerLinePeriodKpi, LineOverallStats } from "@/api/lineScores";
import { getTeamLogo } from "@/lib/utils";

const LINE_COLORS: Record<number, { border: string; bg: string; text: string }> = {
    1: { border: "var(--color-chart-blue)", bg: "rgba(59, 130, 246, 0.1)", text: "var(--color-chart-blue)" },
    2: { border: "#10b981", bg: "rgba(16, 185, 129, 0.1)", text: "#10b981" },
    3: { border: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)", text: "#f59e0b" },
    4: { border: "#ef4444", bg: "rgba(239, 68, 68, 0.1)", text: "#ef4444" },
};

const TEAM_COLORS = {
    chosen:   { border: "var(--color-chart-blue)",    bg: "rgba(59, 130, 246, 0.1)",  text: "var(--color-chart-blue)" },
    opponent: { border: "var(--color-general-orange)", bg: "rgba(249, 115, 22, 0.1)", text: "var(--color-general-orange)" },
} as const;

const LINE_STAT_LABELS = [
    { key: "overallDefenseAVG" as const, label: "Def" },
    { key: "overallOffensiveAVG" as const, label: "Off" },
    { key: "overallTeamPlayAVG" as const, label: "TP" },
    { key: "overallEffortAVG" as const, label: "Eff" },
    { key: "overallBonusAVG" as const, label: "Bon" },
];

const LINE_STAT_MAX: Record<string, number> = {
    overallDefenseAVG: 20,
    overallOffensiveAVG: 25,
    overallTeamPlayAVG: 29,
    overallEffortAVG: 22,
    overallBonusAVG: 7,
};

const kpiToPeriodRow = (p: PlayerLinePeriodKpi, teamId: number): PeriodTableRow => ({
    playerId: p.playerId ?? 0,
    teamId,
    playerName: p.playerName ?? null,
    playerLastName: p.playerLastName ?? null,
    playerRole: p.role,
    pointsDefensive: p.pointsDefensive ?? 0,
    pointsOffensive: p.pointsOffensive ?? 0,
    pointsTeamPlay: p.pointsTeamPlay ?? 0,
    pointsEffort: p.pointsEffort ?? 0,
    pointsBonus: p.pointsBonus ?? 0,
    totalPoints: p.totalPoints ?? 0,
});

const periodRowToStatRow = (row: PeriodTableRow): StatRow => ({
    playerId: row.playerId,
    displayName: `${row.playerName ?? ""} ${row.playerLastName ?? ""}`.trim(),
    role: row.playerRole,
    subScores: [
        { label: "Def", value: row.pointsDefensive },
        { label: "Off", value: row.pointsOffensive },
        { label: "TP", value: row.pointsTeamPlay },
        { label: "Eff", value: row.pointsEffort },
        { label: "Bon", value: row.pointsBonus },
    ],
    totalPoints: row.totalPoints,
});

const StatsBar = ({
    avg,
    side,
    lineColors,
}: {
    avg: LineOverallStats;
    side: "chosen" | "opponent";
    lineColors: { text: string; border: string; bg: string };
}) => {
    const teamColors = TEAM_COLORS[side];
    return (
        <div
            className="rounded-lg border p-3 w-full"
            style={{ borderColor: teamColors.border, backgroundColor: teamColors.bg }}
        >
            <div className="grid grid-cols-6 gap-2">
                {[
                    { label: "Def", value: avg.overallDefenseAVG },
                    { label: "Off", value: avg.overallOffensiveAVG },
                    { label: "TP", value: avg.overallTeamPlayAVG },
                    { label: "Eff", value: avg.overallEffortAVG },
                    { label: "Bon", value: avg.overallBonusAVG },
                ].map(({ label, value }) => (
                    <div key={label} className="text-center">
                        <div className="text-[10px] text-on-surface-variant mb-0.5">{label}</div>
                        <div className="text-sm font-bold text-on-surface">{value || "—"}</div>
                    </div>
                ))}
                <div className="text-center">
                    <div className="text-[10px] font-semibold mb-0.5" style={{ color: lineColors.text }}>
                        Total
                    </div>
                    <div className="text-sm font-extrabold" style={{ color: lineColors.text }}>
                        {avg.overallTotalAVG || "—"}
                    </div>
                </div>
            </div>
        </div>
    );
};

const TeamHeader = ({ teamName, side }: { teamName: string; side: "chosen" | "opponent" }) => {
    const logo = getTeamLogo(teamName);
    const color = TEAM_COLORS[side].text;
    return (
        <div className="flex items-center gap-2 px-1">
            {logo && <img src={logo} alt={teamName} className="h-4 w-auto object-contain flex-shrink-0" />}
            <span className="text-xs font-semibold truncate" style={{ color }} title={teamName}>
                {teamName}
            </span>
        </div>
    );
};

const TeamColumn = ({
    avg,
    teamName,
    players,
    side,
    lineColors,
}: {
    avg: LineOverallStats;
    teamName: string;
    players: PeriodTableRow[];
    side: "chosen" | "opponent";
    lineColors: { border: string; bg: string; text: string };
}) => (
    <div className="flex flex-col gap-2">
        <TeamHeader teamName={teamName} side={side} />
        <div className="md:hidden">
            <StatsMobileCards data={players.map(periodRowToStatRow)} />
        </div>
        <div className="hidden md:block rounded-md overflow-hidden border border-outline-variant/20">
            <CompactStatsTable<PeriodTableRow> columns={periodTableColumns} data={players} />
        </div>
        <StatsBar avg={avg} side={side} lineColors={lineColors} />
    </div>
);

const LinePeriodLineSection = ({
    lineNumber,
    chosenAvg,
    opponentAvg,
    chosenPlayers,
    opponentPlayers,
    chosenTeamName,
    opponentTeamName,
}: {
    lineNumber: number;
    chosenAvg: LineOverallStats;
    opponentAvg?: LineOverallStats;
    chosenPlayers: PeriodTableRow[];
    opponentPlayers?: PeriodTableRow[];
    chosenTeamName: string;
    opponentTeamName?: string;
}) => {
    const colors = LINE_COLORS[lineNumber] ?? LINE_COLORS[1];

    const radarData = useMemo<RadarPoint[]>(
        () =>
            opponentAvg
                ? LINE_STAT_LABELS.map(({ key, label }) => ({
                      stat: label,
                      A: Math.round((chosenAvg[key] / (LINE_STAT_MAX[key] || 1)) * 100),
                      B: Math.round((opponentAvg[key] / (LINE_STAT_MAX[key] || 1)) * 100),
                      fullMark: 100,
                      aRaw: chosenAvg[key],
                      bRaw: opponentAvg[key],
                  }))
                : [],
        [chosenAvg, opponentAvg]
    );

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 px-1">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: colors.text }} />
                <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: colors.text }}>
                    Line {lineNumber}
                </span>
                <div className="flex-1 h-px bg-outline-variant/20" />
            </div>

            {opponentAvg ? (
                <>
                    {/* Mobile: stack vertically, no radar */}
                    <div className="md:hidden flex flex-col gap-4">
                        <TeamColumn
                            avg={chosenAvg}
                            teamName={chosenTeamName}
                            players={chosenPlayers}
                            side="chosen"
                            lineColors={colors}
                        />
                        <TeamColumn
                            avg={opponentAvg}
                            teamName={opponentTeamName ?? "Opponent"}
                            players={opponentPlayers ?? []}
                            side="opponent"
                            lineColors={colors}
                        />
                    </div>
                    {/* Desktop: 3-column with radar */}
                    <div className="hidden md:grid grid-cols-[1fr_280px_1fr] gap-4 items-end">
                        <TeamColumn
                            avg={chosenAvg}
                            teamName={chosenTeamName}
                            players={chosenPlayers}
                            side="chosen"
                            lineColors={colors}
                        />
                        <div className="h-[260px]">
                            <ComparisonEngineChart
                                data={radarData}
                                playerAName={chosenTeamName}
                                playerBName={opponentTeamName ?? "Opponent"}
                            />
                        </div>
                        <TeamColumn
                            avg={opponentAvg}
                            teamName={opponentTeamName ?? "Opponent"}
                            players={opponentPlayers ?? []}
                            side="opponent"
                            lineColors={colors}
                        />
                    </div>
                </>
            ) : (
                <div className="flex flex-col gap-2">
                    <TeamHeader teamName={chosenTeamName} side="chosen" />
                    <div className="md:hidden">
                        <StatsMobileCards data={chosenPlayers.map(periodRowToStatRow)} />
                    </div>
                    <div className="hidden md:block rounded-md overflow-hidden border border-outline-variant/20">
                        <OverviewStatsTable<PeriodTableRow>
                            columns={periodTableColumns}
                            data={chosenPlayers}
                        />
                    </div>
                    <StatsBar avg={chosenAvg} side="chosen" lineColors={colors} />
                </div>
            )}
        </div>
    );
};

interface LinePeriodViewProps {
    period: LinePeriodData | undefined;
    teamNames: { chosen: string; opponent: string };
}

export const LinePeriodView = ({ period, teamNames }: LinePeriodViewProps) => {
    if (!period) {
        return (
            <div className="flex items-center justify-center h-48">
                <span className="text-sm text-on-surface-variant">No line period data available</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-8">
            {([1, 2, 3, 4] as const).map((lineNumber) => {
                const lineKey = `line${lineNumber}` as const;
                const chosenAvg = period.chosenTeamAverage?.lineAvgs?.[lineKey];
                const opponentAvg = period.opponentTeamAverage?.lineAvgs?.[lineKey] ?? undefined;
                if (!chosenAvg) return null;

                const chosenTeamId = period.chosenTeamAverage?.teamId ?? 0;
                const opponentTeamId = period.opponentTeamAverage?.teamId ?? 0;
                const chosenPlayers = (period.chosenTeamAverage?.playersPeriodKpi ?? [])
                    .filter((p) => p.line === lineNumber)
                    .map((p) => kpiToPeriodRow(p, chosenTeamId));
                const opponentPlayers = (period.opponentTeamAverage?.playersPeriodKpi ?? [])
                    .filter((p) => p.line === lineNumber)
                    .map((p) => kpiToPeriodRow(p, opponentTeamId));

                return (
                    <LinePeriodLineSection
                        key={lineNumber}
                        lineNumber={lineNumber}
                        chosenAvg={chosenAvg}
                        opponentAvg={opponentAvg}
                        chosenPlayers={chosenPlayers}
                        opponentPlayers={opponentPlayers}
                        chosenTeamName={teamNames.chosen}
                        opponentTeamName={teamNames.opponent}
                    />
                );
            })}
        </div>
    );
};
